import styled, { css } from 'styled-components';

interface SidebarContainerProps {
  expanded: boolean;
}

export const SidebarContainer = styled.aside<SidebarContainerProps>`
  width: ${(props) => (props.expanded ? '215px' : '60px')};
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 90;
`;

export const SidebarHeader = styled.div`
  background-color: ${({ theme }) => theme.primaryColor};
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  height: 50px;
`;

export const SidebarBody = styled.div`
  background-color: ${({ theme }) => theme.backgroundColor};
  color: ${({ theme }) => theme.textColor};
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
  border-right: 1px solid ${({ theme }) => theme.lightGray};
`;

export const SidebarIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  overflow: hidden;
`;

export const SearchContainer = styled.div`
  padding: 12px;
  position: relative;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  padding-right: 32px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: ${({ theme }) => theme.backgroundColor};
  color: ${({ theme }) => theme.textColor};
  font-size: 14px;

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primaryColor};
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
  }
`;

export const SearchIcon = styled.div`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

export const MenuList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex-grow: 1;
  overflow-y: auto;
`;

/**
 * Fundo do bloco de Super Admin.
 *
 * Ambar de proposito, e nao o verde da marca: o objetivo e o bloco NAO se
 * confundir com o menu comum. Opacidade baixa para funcionar sobre o claro e
 * sobre o escuro sem virar dois tokens de tema.
 */
const fundoDestaque = 'rgba(255, 152, 0, 0.12)';
const bordaDestaque = 'rgba(255, 152, 0, 0.38)';

/**
 * Lista ancorada no rodape, logo acima do Sair.
 *
 * Sem `flex-grow`: quem estica e a MenuList principal, e e justamente isso que
 * empurra este bloco para baixo.
 */
export const MenuListRodape = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 8px;
  flex-shrink: 0;
`;

/** Rotulo do item de primeiro nivel; nao quebra em duas linhas. */
export const MenuItemLabel = styled.span`
  white-space: nowrap;
`;

export const MenuItem = styled.li<{ destaque?: boolean }>`
  margin: 0;
  padding: 0;
  position: relative;

  ${({ destaque }) =>
    destaque &&
    css`
      padding: 4px 6px 0;
      border: 1px solid ${bordaDestaque};
      border-bottom: none;
      border-radius: 10px 10px 0 0;
      background-color: ${fundoDestaque};
    `}
`;

export const MenuItemLink = styled.a`
  display: flex;
  align-items: center;
  padding: 12px 15px;
  color: ${({ theme }) => theme.textColor};
  text-decoration: none;
  transition: background-color 0.2s;
  border-radius: 4px;
  gap: 10px;
  font-size: 14px;

  &:hover,
  &.active {
    background-color: ${({ theme }) => theme.lightGrayHover};
  }

  &.active {
    font-weight: 600;
  }
`;

export const MenuItemButton = styled.button`
  display: flex;
  align-items: center;
  padding: 12px 15px;
  color: ${({ theme }) => theme.textColor};
  transition: background-color 0.2s;
  border-radius: 4px;
  gap: 10px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 14px;

  &:hover{
    background-color: ${({ theme }) => theme.lightGrayHover};
  }

  &.active {
    font-weight: 600;
  }
`;

export const MenuItemArrow = styled.span<{ expanded: boolean }>`
  position: absolute;
  right: 15px;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid ${({ theme }) => theme.textColor};
  transition: transform 0.2s;


  ${(props) =>
    props.expanded &&
    css`
      transform: rotate(180deg);
    `}
`;

export const MenuItemIcon = styled.div`
  width: 20px;
  display: flex;
  justify-content: center;
`;

export const SubMenuList = styled.ul<{ destaque?: boolean }>`
  list-style: none;
  padding: 0;
  margin: 0 0 0 20px;

  /* continua o bloco do cabecalho: junto, os dois formam um cartao so */
  ${({ destaque }) =>
    destaque &&
    css`
      margin: 0;
      padding: 0 6px 6px 20px;
      border: 1px solid ${bordaDestaque};
      border-top: none;
      border-radius: 0 0 10px 10px;
      background-color: ${fundoDestaque};
    `}
`;

/** Selo ao lado do titulo do grupo restrito. */
export const SeloDestaque = styled.span`
  margin-left: auto;
  padding: 1px 5px;
  border-radius: 999px;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.3px;
  white-space: nowrap;
  text-transform: uppercase;
  color: ${({ theme }) => theme.warningColor};
  border: 1px solid ${bordaDestaque};
`;

export const SubMenuItem = styled.li`
  margin: 5px 0;
`;

export const SubMenuItemLink = styled.a`
  display: flex;
  align-items: center;
  padding: 8px 15px;
  color: ${({ theme }) => theme.textColor};
  text-decoration: none;
  transition: background-color 0.2s;
  border-radius: 4px;
  gap: 10px;
  font-size: 0.9rem;

  &:hover {
    background-color: ${({ theme }) => theme.lightGrayHover};
  }

  &.active {
    font-weight: 600;
  }
`;

export const SubMenuItemIcon = styled.div`
  width: 16px;
  display: flex;
  justify-content: center;
  font-size: 0.8rem;
`;

export const EmpresaText = styled.span`
  margin-left: 0.5rem;
  font-size: 14px;
  font-weight: 500;
`;

export const Empresa = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
`;

export const EmpresaIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DataBase = styled.div<{ dataBase: string }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  font-size: 14px;
  background-color: ${({ theme, dataBase }) => dataBase.includes('ada')  ? theme.primaryColor : dataBase.includes('altinfo') ? theme.secondaryColor : theme.backgroundColor};
  color: ${({ theme, dataBase }) => dataBase.includes('ada')  ? '#fff' : dataBase.includes('altinfo') ? '#fff': theme.textColor};
`;

export const DataBaseIcon = styled.div`
  width: 20px;
  height: 20px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;


export const MenuLogoutIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LogoutButton = styled.button`
  background-color: ${({ theme }) => theme.secondaryColor};
  display: flex;
  align-items: center;
  padding: 16px;
  border: none;
  border-radius: 0;
  color: #fff;
  cursor: pointer;
  margin-top: auto;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  span {
    margin-left: 12px;
    white-space: nowrap;
  }

  &:hover {
    background-color: ${({ theme }) => theme.secondaryColorHover};
  }
`;

interface FloatingSubMenuProps {
  top: number;
}

export const FloatingSubMenu = styled.div<FloatingSubMenuProps>`
  position: fixed;
  top: ${({ top }) => `${top}px`};
  left: 60px;
  background-color: ${({ theme }) => theme.backgroundColor};
  border: 1px solid ${({ theme }) => theme.lightGray};
  border-left: none;
  border-radius: 0 4px 4px 0;
  z-index: 100;
  min-width: 200px;
  box-shadow: 4px 4px 16px rgba(0, 0, 0, 0.1);
  padding: 8px 0;
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;
